import React from 'react'
import styled from 'styled-components'
import { UIStyles } from '../../theme'
import { Link } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { StyledTooltip } from '../UIComponents/StyledTooltip'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { TOP_BAR_NAV_COLLAPSE_WIDTH, TOP_BAR_NAV_COLLAPSE_WIDTH_DASHBOARD } from '../../constants'
import NavMenu from './NavMenu'
import LayiMark, { NAV_LINK_CLASS } from './LayiMark'
import { ReactComponent as RegionalIntelIcon } from '../../assets/img/regional-intel-icon.svg'
import { trackRegionalIntelClicked } from '../../regionalIntel/regionalIntelTracking'

// The page switcher of the top bar: viewer, dashboard, BI Labs, Regional Intel and LAYI. One list of
// entries, two renderings — the icon row from TOP_BAR_NAV_COLLAPSE_WIDTH up (…_DASHBOARD on the
// dashboard, whose filters share the bar), a hamburger menu with titled entries below it. Both are
// rendered and their media queries, fed the same width, show one, so no resize listener is needed.
// Regional Intel is another site: a plain link that opens in a new tab, so it is never the current page.

// Size of an `svgIcon` in the row. Regional Intel's outline fills about 70% of its 28px box, a Font
// Awesome glyph nearly all of its em, so this sits with the 35px glyphs. UX tunes it from screenshots.
const SVG_ICON_SIZE = 40

const Wrapper = styled.div`
  transition: right 0.3s ease-in-out;
  display: inline-flex;
  height: 100%;
`

const IconRow = styled(Wrapper)`
  @media (max-width: ${props => props.collapseWidth - 1}px) {
    display: none;
  }
`

const IconWrapper = styled(Wrapper)`
  background-color: transparent;
  transition: all 0.2s ease;
  border-radius: 4px;
  &,
  > a {
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
    text-decoration: none;
  }
  &:hover {
    background-color: ${props => !props.disabled && themeColors.whiteTransparent01};
    transform: translateY(-1px);
  }
`

// An SVG drawn in currentColor, idle like the glyphs; StyledLink lights both up on hover.
const SvgIcon = styled.span`
  display: flex;
  color: ${UIStyles.lightGrey};
  transition: all 0.2s ease;
`

const StyledLink = styled(Link)`
  height: 100%;
  width: 56px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease;
  &:focus:not(:focus-visible) {
    outline: none;
  }
  /* The rounded IconWrapper paints the hover tint; a second one here doubled it. The glyph (or SVG)
     is a descendant, not a child: the tooltip wraps it in a <div>. */
  &:hover {
    i,
    ${SvgIcon} {
      color: ${props => !props.disabled && UIStyles.whiteColor};
      transform: scale(1.1) translateY(-1px);
    }
  }
`

// Another site's entry: the same box as a page's, as a plain <a> (styled-components v3).
const ExternalLink = StyledLink.withComponent('a')

const StyledIcon = styled.i`
  font-size: 35px;
  color: ${props => (props.disabled ? UIStyles.whiteColor : UIStyles.lightGrey)};
  transition: all 0.2s ease;
`

const TooltipName = styled.span`
  display: block;
  font-weight: bold;
  text-align: center;
`

const TooltipSubtitle = styled.span`
  display: block;
  font-size: 11px;
  color: ${themeColors.whiteTransparent08};
  text-align: center;
`

// The pages, in bar order, with today's visibility rules. Titles are the page names as such, not
// translated (like the routes and the LAYI and Regional Intel brands); a `subtitle` says what the page
// is, under the name in the tooltip. `icon` is a Font Awesome class; the LAYI entry draws the brand mark
// instead and `svgIcon` is an SVG component drawn in currentColor. An entry with an `href` is another
// site, opened in a new tab. Ids and test ids are what the Cypress and Robot suites click.
export const buildNavEntries = ({
  t,
  isLayers,
  isDashboard,
  isBiLabs,
  isChat,
  biLabsUrl,
  hasRoleDeactivateDashboard,
  canUseLayi,
  regionalIntelUrl,
  onLayersSwitch
}) =>
  [
    { key: 'layers', route: 'layers', title: 'Layers', icon: 'fa fa-regular fa-map', active: isLayers, onClick: onLayersSwitch, testId: 'icon-visor' },
    !hasRoleDeactivateDashboard && {
      key: 'dashboard',
      route: 'dashboard',
      title: 'Dashboard',
      icon: 'fa fa-chart-column',
      active: isDashboard,
      id: 'link-dashboard',
      testId: 'icon-dashboard'
    },
    !isEmpty(biLabsUrl) && { key: 'bilabs', route: 'bilabs', title: 'BI Labs', icon: 'fa fa-chart-bar', active: isBiLabs },
    !isEmpty(regionalIntelUrl) && {
      key: 'regional-intel',
      href: regionalIntelUrl,
      title: 'Regional Intel',
      subtitle: t('regional_intel_subtitle'),
      svgIcon: RegionalIntelIcon,
      onClick: trackRegionalIntelClicked,
      testId: 'icon-regional-intel'
    },
    canUseLayi && { key: 'chat', route: 'chat', title: 'LAYI', mark: true, active: isChat, id: 'link-chat', testId: 'icon-layi' }
  ].filter(Boolean)

// A page by its route; another site in a new tab, never the current page. That one is named here: its
// two-line tooltip title is not a string, so MUI does not name the link with it.
const linkProps = (entry, generateUrl) =>
  entry.href
    ? { href: entry.href, target: '_blank', rel: 'noopener noreferrer', 'aria-label': entry.title }
    : { to: generateUrl(entry.route), disabled: entry.active }

const tooltipTitle = entry =>
  entry.subtitle ? (
    <>
      <TooltipName>{entry.title}</TooltipName>
      <TooltipSubtitle>{entry.subtitle}</TooltipSubtitle>
    </>
  ) : (
    entry.title
  )

const renderIcon = entry => {
  if (entry.mark) return <LayiMark active={entry.active} testId={entry.testId} />
  if (entry.svgIcon) {
    const Svg = entry.svgIcon
    return (
      <SvgIcon data-testid={entry.testId}>
        <Svg width={SVG_ICON_SIZE} height={SVG_ICON_SIZE} aria-hidden="true" focusable="false" />
      </SvgIcon>
    )
  }
  return <StyledIcon data-testid={entry.testId} className={entry.icon} disabled={entry.active} />
}

export const DashboardViewerToggler = props => {
  const {
    t,
    locale,
    isLayers,
    isDashboard,
    isBiLabs,
    isChat,
    selectFields,
    selectedFieldsLength,
    biLabsUrl,
    hasRoleDeactivateDashboard,
    canUseLayi,
    regionalIntelUrl
  } = props
  const generateUrl = route => (locale !== 'es' ? '/' + locale : '') + '/' + route
  const onLayersSwitch = () => {
    if (selectedFieldsLength > 0) {
      selectFields()
    }
  }

  const collapseWidth = isDashboard ? TOP_BAR_NAV_COLLAPSE_WIDTH_DASHBOARD : TOP_BAR_NAV_COLLAPSE_WIDTH
  const entries = buildNavEntries({
    t,
    isLayers,
    isDashboard,
    isBiLabs,
    isChat,
    biLabsUrl,
    hasRoleDeactivateDashboard,
    canUseLayi,
    regionalIntelUrl,
    onLayersSwitch
  })

  return (
    <Wrapper id="DashboardViewerToggler">
      <IconRow collapseWidth={collapseWidth}>
        {entries.map(entry => {
          const EntryLink = entry.href ? ExternalLink : StyledLink
          return (
            <IconWrapper key={entry.key} disabled={entry.active}>
              <EntryLink className={NAV_LINK_CLASS} onClick={entry.onClick} id={entry.id} {...linkProps(entry, generateUrl)}>
                <StyledTooltip title={tooltipTitle(entry)}>{renderIcon(entry)}</StyledTooltip>
              </EntryLink>
            </IconWrapper>
          )
        })}
      </IconRow>

      <NavMenu entries={entries} generateUrl={generateUrl} t={t} collapseWidth={collapseWidth} />
    </Wrapper>
  )
}
